import { useUserRole } from 'hooks/useUserRole'
import { CardButtonProps } from 'components/IntegrationsModal/components/IntegrationCards/Types'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { ManageIntegrationAccounts } from 'components/IntegrationsModal/ManageIntegrationAccounts/ManageIntegrationAccounts'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ArrowRefresh2, Close } from 'blaze-ui/icons'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useConnectedAccountCardProps } from 'components/IntegrationsModal/ManageIntegrationAccounts/hooks'
import { Spinner } from 'common/Spinner/Spinner'
import { Tooltip } from 'components/Tooltip/Tooltip'
import Styles from '../ExternalIntegrationCard/ExternalIntegrationCard.module.scss'
import cx from 'classnames'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const CardButtonsForVirtualMarketer = ({
  integration,
  isDisconnected = false,
  isOutdated = false,
}: {
  integration: ExternalIntegration
  isDisconnected?: boolean
  isOutdated?: boolean
}) => {
  const { onRefreshConnection, onDisconnectAccount, isDeleting } = useConnectedAccountCardProps(integration)
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const buttonVariant = isSystemRedesignEnabled ? 'secondary' : 'tertiary'
  const buttonSize = isSystemRedesignEnabled ? 'md' : 'sm'

  return (
    <div className={Styles.actions}>
      {integration.provider !== ExternalIntegrationEnum.WordPress && (
        <Button
          variant={buttonVariant}
          size={buttonSize}
          onPress={() => onRefreshConnection()}
          isDisabled={isDeleting}
          frontIcon={ArrowRefresh2}
          className={cx({
            [Styles.disconnectedRefreshButton]: isDisconnected,
            [Styles.outdatedRefreshButton]: isOutdated,
          })}
        >
          Refresh
        </Button>
      )}
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onPress={onDisconnectAccount}
        isDisabled={isDeleting}
        frontIcon={isDeleting ? undefined : Close}
      >
        {isDeleting ? (
          <div className={Styles.loadingState}>
            <Spinner />
            <span>Disconnecting...</span>
          </div>
        ) : (
          'Disconnect'
        )}
      </Button>
    </div>
  )
}

export const CardButtons = ({
  isConnected,
  onConnect,
  provider,
  integration,
  isDisabled = false,
  tooltipMessage,
  isDisconnected = false,
  isOutdated = false,
}: CardButtonProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const userRole = useUserRole()
  const { openModal } = useModals()
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  const buttonVariant = isSystemRedesignEnabled ? 'secondary' : 'tertiary'
  const buttonSize = isSystemRedesignEnabled ? 'md' : 'sm'

  if (!userRole) {
    return null
  }

  if (!isConnected) {
    const connectButton = (
      <Button size={buttonSize} onPress={onConnect} fullWidth variant={buttonVariant} isDisabled={isDisabled}>
        Connect
      </Button>
    )

    if (isDisabled && tooltipMessage) {
      return (
        <Tooltip label={tooltipMessage} placement="bottom" mouseEnterDelay={0}>
          <div className={Styles.tooltipWrapper}>{connectButton}</div>
        </Tooltip>
      )
    }

    return connectButton
  }

  if (isVirtualMarketer) {
    return (
      <CardButtonsForVirtualMarketer
        integration={integration}
        isDisconnected={isDisconnected}
        isOutdated={isOutdated}
      />
    )
  }

  return (
    <Button
      onPress={() => openModal(ManageIntegrationAccounts, { provider })}
      variant={buttonVariant}
      size={buttonSize}
      fullWidth
    >
      Manage Integration
    </Button>
  )
}
